import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { X, Download, Loader2, Image as ImageIcon, FileImage } from 'lucide-react';
import { toast } from 'sonner';

const DownloadFormatModal = ({ 
  isOpen, 
  onClose, 
  imageUrl,
  defaultFilename = 'mockuppro-image'
}) => {
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState('high');
  const [downloading, setDownloading] = useState(false);

  const formats = [
    { id: 'png', label: 'PNG', description: 'Lossless, transparent background support', icon: '🖼️' },
    { id: 'jpg', label: 'JPG', description: 'Smaller file size, best for photos', icon: '📷' },
  ];

  const qualities = [
    { id: 'high', label: 'High Quality', value: 1.0 },
    { id: 'medium', label: 'Medium Quality', value: 0.8 },
    { id: 'low', label: 'Low Quality (Smaller file)', value: 0.6 },
  ];

  const handleDownload = async () => {
    if (!imageUrl) {
      toast.error('No image available to download');
      return;
    }

    setDownloading(true);

    try {
      let downloadUrl = imageUrl;
      let filename = `${defaultFilename}-${Date.now()}.${format}`;

      // If format conversion is needed or it's a URL image
      if (format === 'jpg' || !imageUrl.startsWith('data:')) {
        // Create canvas to convert format
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        
        // For JPG, fill with white background (no transparency)
        if (format === 'jpg') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        
        ctx.drawImage(img, 0, 0);

        const qualityValue = qualities.find(q => q.id === quality)?.value || 1.0;
        const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png';
        
        downloadUrl = canvas.toDataURL(mimeType, qualityValue);
      }

      // Download the file
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Downloaded as ${format.toUpperCase()}`);
      onClose();
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-background rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="download-format-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-semibold">Download Image</h3>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-muted rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Format Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">File Format</Label>
              <RadioGroup value={format} onValueChange={setFormat} className="space-y-2">
                {formats.map((f) => (
                  <label
                    key={f.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all
                      ${format === f.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-muted-foreground/30'}`}
                    data-testid={`format-${f.id}`}
                  >
                    <RadioGroupItem value={f.id} id={f.id} />
                    <span className="text-xl">{f.icon}</span>
                    <div className="flex-1">
                      <p className="font-medium">{f.label}</p>
                      <p className="text-xs text-muted-foreground">{f.description}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Quality Selection (only for JPG) */}
            {format === 'jpg' && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Quality</Label>
                <RadioGroup value={quality} onValueChange={setQuality} className="flex gap-2">
                  {qualities.map((q) => (
                    <label
                      key={q.id}
                      className={`flex-1 text-center py-2 px-3 rounded-lg border cursor-pointer transition-all text-sm
                        ${quality === q.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-muted-foreground/30'}`}
                    >
                      <RadioGroupItem value={q.id} id={q.id} className="sr-only" />
                      {q.label}
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Preview Info */}
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
              <FileImage className="w-10 h-10 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  {defaultFilename}.{format}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format === 'png' ? 'Best for graphics & transparency' : 'Best for photos & smaller files'}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              className="flex-1" 
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-confirm-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download {format.toUpperCase()}
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DownloadFormatModal;
